/**
 * Community Me / Profile page — /community/me/profile
 *
 * Reads + edits the current user's community profile. Lazy-creates
 * the row server-side on first GET, so this page handles a fresh
 * profile with all-default fields gracefully.
 *
 * Form fields (per ProfileEditBody): handle, headline, bio,
 * specialties (comma-separated input), region, website_url,
 * linkedin_url, is_listed, credential_visible, dm_pref.
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  fetchMyProfile,
  updateMyProfile,
  type DmPref,
  type ProfileEditBody,
  type ProfileRead,
} from "@/lib/community-api";
import { MarkdownComposer } from "@/components/MarkdownComposer";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; profile: ProfileRead }
  | { kind: "not_member" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function joinSpecialties(specialties: string[]): string {
  return specialties.join(", ");
}

function parseSpecialties(input: string): string[] {
  return input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, 12);
}

export interface CommunityMyProfilePageProps {
  fetchFn?: typeof fetchMyProfile;
  updateFn?: typeof updateMyProfile;
  onUnauthenticated?: (returnTo: string) => void;
}

export function CommunityMyProfilePage({
  fetchFn = fetchMyProfile,
  updateFn = updateMyProfile,
  onUnauthenticated = redirectToLogin,
}: CommunityMyProfilePageProps = {}): React.JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [draft, setDraft] = useState<ProfileEditBody>({});
  const [specialtiesInput, setSpecialtiesInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await fetchFn();
        if (cancelled) return;
        setState({ kind: "ready", profile });
        setSpecialtiesInput(joinSpecialties(profile.specialties));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated("/community/me/profile");
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setState({ kind: "not_member" });
          return;
        }
        const msg =
          err instanceof Error
            ? err.message
            : "Could not load your profile.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFn, onUnauthenticated]);

  const handleSave = async (): Promise<void> => {
    if (state.kind !== "ready") return;
    setSaving(true);
    setSaveError(null);
    try {
      const body: ProfileEditBody = { ...draft };
      // Only send specialties if the input differs from what's persisted.
      const parsed = parseSpecialties(specialtiesInput);
      if (
        parsed.length !== state.profile.specialties.length ||
        parsed.some((s, i) => s !== state.profile.specialties[i])
      ) {
        body.specialties = parsed;
      }
      // If nothing in the draft was changed, surface a friendly inline note
      if (Object.keys(body).length === 0) {
        setSaveError("No changes to save.");
        setSaving(false);
        return;
      }
      const updated = await updateFn(body);
      setState({ kind: "ready", profile: updated });
      setDraft({});
      setSpecialtiesInput(joinSpecialties(updated.specialties));
      setSavedAt(Date.now());
    } catch (err) {
      if (err instanceof ApiError && err.body) {
        const eb = err.body as {
          detail?: { blocking?: string; message?: string };
        };
        setSaveError(eb.detail?.message ?? eb.detail?.blocking ?? err.message);
      } else if (err instanceof Error) {
        setSaveError(err.message);
      } else {
        setSaveError("Could not save your changes.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading your profile…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="mt-4 text-muted-foreground">
          The community is open to active learners and certified
          practitioners.
        </p>
        <Link
          to="/community"
          className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back to community
        </Link>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { profile } = state;

  // Helper: current value = draft override if set, else profile value.
  const v = <K extends keyof ProfileEditBody>(
    key: K,
    fallback: ProfileEditBody[K],
  ): NonNullable<ProfileEditBody[K]> => {
    const dv = draft[key];
    return (dv ?? fallback) as NonNullable<ProfileEditBody[K]>;
  };

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community" className="hover:text-foreground">
          Community
        </Link>{" "}
        <span aria-hidden="true">›</span> My profile
      </nav>

      <header className="mt-3 mb-8">
        <h1 className="text-3xl font-bold text-foreground">My profile</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Public-facing information shown alongside your posts and (when
          opted in) in the member directory. Initials-only display until
          you opt in to public-name display.
        </p>
        {profile.display_name && (
          <p className="mt-2 text-sm text-muted-foreground">
            Display name: <strong>{profile.display_name}</strong>
            {profile.is_certified && " · ✓ Certified"}
          </p>
        )}
      </header>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <div>
          <label
            htmlFor="prof-handle"
            className="block text-sm font-medium text-foreground"
          >
            Handle (optional)
          </label>
          <input
            id="prof-handle"
            type="text"
            value={v("handle", profile.handle ?? "")}
            onChange={(e) => {
              setDraft({ ...draft, handle: e.target.value });
            }}
            placeholder="lowercase-letters, digits, underscore, hyphen"
            minLength={2}
            maxLength={32}
            className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Used in your public URL: /community/members/&lt;handle&gt;
          </p>
        </div>

        <div>
          <label
            htmlFor="prof-headline"
            className="block text-sm font-medium text-foreground"
          >
            Headline
          </label>
          <input
            id="prof-headline"
            type="text"
            value={v("headline", profile.headline ?? "")}
            onChange={(e) => {
              setDraft({ ...draft, headline: e.target.value });
            }}
            maxLength={160}
            placeholder="e.g. Rural psychiatrist, NC"
            className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <MarkdownComposer
          label="Bio"
          value={v("bio", profile.bio ?? "")}
          onChange={(value) => {
            setDraft({ ...draft, bio: value });
          }}
          maxLength={4_000}
          placeholder="Tell people what you do and where you do it."
          rows={5}
        />

        <div>
          <label
            htmlFor="prof-specialties"
            className="block text-sm font-medium text-foreground"
          >
            Specialties
          </label>
          <input
            id="prof-specialties"
            type="text"
            value={specialtiesInput}
            onChange={(e) => {
              setSpecialtiesInput(e.target.value);
            }}
            placeholder="ptsd, rural-care, primary-care"
            className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Comma-separated. Up to 12 tags. Used in directory search filters.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="prof-region"
              className="block text-sm font-medium text-foreground"
            >
              Region
            </label>
            <input
              id="prof-region"
              type="text"
              value={v("region", profile.region ?? "")}
              onChange={(e) => {
                setDraft({ ...draft, region: e.target.value });
              }}
              maxLength={80}
              placeholder="State or country only"
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label
              htmlFor="prof-website"
              className="block text-sm font-medium text-foreground"
            >
              Website
            </label>
            <input
              id="prof-website"
              type="url"
              value={v("website_url", profile.website_url ?? "")}
              onChange={(e) => {
                setDraft({ ...draft, website_url: e.target.value });
              }}
              maxLength={300}
              placeholder="https://"
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="prof-linkedin"
              className="block text-sm font-medium text-foreground"
            >
              LinkedIn
            </label>
            <input
              id="prof-linkedin"
              type="url"
              value={v("linkedin_url", profile.linkedin_url ?? "")}
              onChange={(e) => {
                setDraft({ ...draft, linkedin_url: e.target.value });
              }}
              maxLength={300}
              placeholder="https://www.linkedin.com/in/…"
              className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-2 text-sm font-medium text-foreground">
            Visibility
          </legend>
          <label className="flex items-start gap-3 text-sm text-foreground">
            <input
              type="checkbox"
              checked={v("is_listed", profile.is_listed)}
              onChange={(e) => {
                setDraft({ ...draft, is_listed: e.target.checked });
              }}
              className="mt-1"
            />
            <span>
              <strong>List me in the directory.</strong>{" "}
              <span className="text-muted-foreground">
                Other members can find you by handle, specialty, or region.
                Default: off.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm text-foreground">
            <input
              type="checkbox"
              checked={
                v("credential_visible", profile.credential_visible)
              }
              onChange={(e) => {
                setDraft({ ...draft, credential_visible: e.target.checked });
              }}
              className="mt-1"
            />
            <span>
              <strong>Show my credential badge.</strong>{" "}
              <span className="text-muted-foreground">
                Surfaces ✓ Certified next to your name on posts + in the
                directory.
              </span>
            </span>
          </label>
          <div className="text-sm text-foreground">
            <span className="block font-medium">Who can DM me</span>
            <div className="mt-2 flex gap-2">
              {(["everyone", "nobody"] as const).map((option) => {
                const current = v("dm_pref", profile.dm_pref) as DmPref;
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={current === option}
                    onClick={() => {
                      setDraft({ ...draft, dm_pref: option });
                    }}
                    className={`rounded-md border px-3 py-1 text-xs font-medium ${
                      current === option
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {option === "everyone"
                      ? "Any member"
                      : "Nobody (turn off DMs)"}
                  </button>
                );
              })}
            </div>
          </div>
        </fieldset>

        {saveError && (
          <p className="text-sm text-red-700" role="alert">
            {saveError}
          </p>
        )}
        {savedAt !== null && saveError === null && (
          <p className="text-sm text-green-700" role="status">
            Saved.
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </section>
  );
}
